import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { type Search, SearchType } from '@tyro/api';
import { Avatar } from '@tyro/core/avatar';
import { useTranslation } from '@tyro/i18n';
import { useLocation } from '@tyro/router/hooks';
import type { JSX } from 'react';
import { getSearchPathWithPreservedTab } from '../../../utils/search-paths';
import { SearchFavoriteIcon } from '../favourite-icon';
import { SearchOption } from '../option';
import { type SearchOptionData, useSearchProvider } from '../provider';
import { SectionContainer } from '../section-container';

interface PeopleSectionProps {
  people: Search[];
}

const personBasePaths: Partial<Record<SearchType, string>> = {
  [SearchType.Student]: '/people/students',
  [SearchType.Staff]: '/people/staff',
  [SearchType.Contact]: '/people/contacts',
};

export function PersonOption({
  option,
  endIcon,
}: {
  option: SearchOptionData;
  endIcon?: JSX.Element;
}) {
  const { partyId, type, text, avatarUrl } =
    option as PeopleSectionProps['people'][number];
  const [t] = useTranslation(['common']);
  const { pathname } = useLocation();

  const personPath = getSearchPathWithPreservedTab(
    personBasePaths,
    partyId,
    type,
    pathname,
    '/groups/class',
  );

  return (
    <SearchOption path={personPath} optionData={option}>
      <Stack
        direction="row"
        spacing={1}
        alignItems="center"
        justifyContent="space-between"
      >
        <Stack direction="row" spacing={1} alignItems="center">
          <Box
            sx={{
              width: 40,
              height: 40,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <Avatar
              sx={{ width: 32, height: 32, fontSize: 14 }}
              name={text}
              src={avatarUrl}
            />
          </Box>
          <Stack>
            <Typography component="span" variant="body2">
              {text}
            </Typography>
            <Typography component="span" variant="caption">
              {t(`common:searchType.${type}`)}
            </Typography>
          </Stack>
        </Stack>
        {endIcon && <Box alignSelf="flex-end">{endIcon}</Box>}
      </Stack>
    </SearchOption>
  );
}

export function PeopleSection({ people }: PeopleSectionProps) {
  const { t } = useTranslation(['common']);
  const { favouriteIds, toggleFavorite } = useSearchProvider();
  if (people.length === 0) return null;

  return (
    <SectionContainer heading={t('common:people')}>
      {people?.map((option) => {
        const isFavorite = favouriteIds.has(option.partyId);
        return (
          <PersonOption
            key={`${option.partyId}-${option.text}`}
            option={option}
            endIcon={
              <SearchFavoriteIcon
                isFavorite={isFavorite}
                onClick={() => toggleFavorite(option.partyId, option)}
              />
            }
          />
        );
      })}
    </SectionContainer>
  );
}
